import React, { useEffect, useState } from 'react';
import {
  CreateBase,
  SimpleForm,
  TextInput,
  useNotify,
  useRedirect,
  useCreate,
  required,
  useDataProvider,
  SelectInput
} from "react-admin";
import Box from "@mui/material/Box";
import { useFormContext, useWatch } from 'react-hook-form';
import { checkPasswordAgainstPolicy, getPasswordPolicyConfig, PasswordPolicyConfig } from '../../../services/userService.ts';

// Must live inside SimpleForm so it has access to the form context
const EmailUsernameSync: React.FC = () => {
  const { setValue } = useFormContext();
  const email: string = useWatch({ name: 'email' }) || '';

  useEffect(() => {
    setValue('userName', email.split('@')[0] || '', { shouldValidate: false, shouldDirty: true });
  }, [email, setValue]);

  return null;
};

// Must live inside SimpleForm so it has access to the form context via useWatch
const PasswordField: React.FC<{ policy: PasswordPolicyConfig | null }> = ({ policy }) => {
  // Returning a string here makes react-admin mark the field invalid (red border +
  // red helper text), same mechanism the Email field's required() validator uses.
  const validatePassword = (value: string) => {
    if (!policy) return undefined;
    return checkPasswordAgainstPolicy(value, policy) || undefined;
  };

  return (
    <TextInput
      source="password"
      label="Password "
      type="password"
      fullWidth
      validate={[required(), validatePassword]}
    />
  );
};

const UserCreate: React.FC = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const [create] = useCreate();

  const [roles, setRoles] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicyConfig | null>(null);

  useEffect(() => {
    getPasswordPolicyConfig().then(setPasswordPolicy).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      dataProvider.getList('permissionRoles', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: {}
      }),
      dataProvider.getList('permissionDepartment', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: {}
      })
    ])
      .then(([rolesRes, departmentsRes]) => {
        setRoles(rolesRes.data);
        setDepartments(departmentsRes.data);
        setLoading(false);
      })
      .catch((e) => {
        console.error(e);
        notify('Error loading data', { type: 'warning' });
        setLoading(false);
      });
  }, [dataProvider, notify]);

  useEffect(() => {
    if (!selectedDepartmentId) {
      setPositions([]);
      return;
    }
    dataProvider
      .getList('permissionPosition', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: { departmentId: selectedDepartmentId },
      })
      .then((res: any) => {
        setPositions(res.data);
      })
      .catch((e: any) => {
        console.error(e);
        notify('Error loading positions', { type: 'warning' });
        setPositions([]);
      });
  }, [selectedDepartmentId, dataProvider, notify]);

  if (loading) return <span>Loading...</span>;

  const handleSave = async (data: any) => {
    try {
      let password = data.password;

      const policy = passwordPolicy ?? await getPasswordPolicyConfig();
      if (checkPasswordAgainstPolicy(password, policy)) {
        // Violation is already shown live under the field itself — no toast needed.
        return;
      }

      const enc = new TextEncoder();
      const pwBuf = enc.encode(password);
      const hashBuf = await crypto.subtle.digest('SHA-256', pwBuf);
      const hashArray = Array.from(new Uint8Array(hashBuf));
      const passwordHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      password = undefined;

      const payload = {
        userName: (data.email || '').split('@')[0] || data.userName || '',
        email: data.email,
        password: passwordHash,
        departmentId: data.departmentId,
        departmentPositionId: data.departmentPositionId,
        roleId: data.roleId,
        isActive: data.isActive !== undefined ? data.isActive : true,
      };

      await create('permissionUsers', { data: payload });
      notify('User created successfully', { type: 'success' });
      setTimeout(() => {
        redirect('list', 'permissionUsers');
      }, 500);
    } catch (error) {
      console.error('Create user error:', error);
      notify('Create failed', { type: 'error' });
    }
  };

  return (
    <CreateBase resource="permissionUsers">
      <SimpleForm
        sx={{ backgroundColor: '#ffffff', padding: '36px' }}
        onSubmit={handleSave}
        defaultValues={{ email: '', userName: '', password: '' }}
      >
        <EmailUsernameSync />
        <Box display="flex" flexWrap="wrap" gap={2}>

          <Box flex="1 1 48%">
            <TextInput
              source="email"
              label="Email "
              fullWidth
              validate={[required()]}
            />
          </Box>

          <Box flex="1 1 48%">
            <TextInput
              source="userName"
              label="User Name (auto)"
              fullWidth
              slotProps={{ input: { readOnly: true } }}
              sx={{ '& .MuiInputBase-input': { color: '#64748b', cursor: 'default' } }}
            />
          </Box>

          <Box flex="1 1 48%">
            <PasswordField policy={passwordPolicy} />
          </Box>

          <Box flex="1 1 48%">
            <SelectInput
              source="departmentId"
              label="Department"
              choices={departments}
              optionText="departmentName"
              optionValue="id"
              fullWidth
              validate={[required()]}
              onChange={(event) => {
                const value = event.target.value;
                const deptId = typeof value === 'string' ? Number(value) : value;
                setSelectedDepartmentId(deptId);
              }}
            />
          </Box>

          <Box flex="1 1 48%">
            <SelectInput
              source="departmentPositionId"
              label="Position Title"
              choices={positions}
              optionText="positionName"
              optionValue="id"
              fullWidth
              validate={[required()]}
              disabled={!selectedDepartmentId || positions.length === 0}
            />
          </Box>

          <Box flex="1 1 48%">
            <SelectInput
              source="roleId"
              label="Role"
              choices={roles}
              optionText="roleName"
              optionValue="id"
              fullWidth
              validate={[required()]}
            />
          </Box>

        </Box>
      </SimpleForm>
    </CreateBase>
  );
};

export default UserCreate;
